import { KwelloSvgIcon, KWELLO_ICON } from '../icon/KwelloSvgIcon';
import { TagEditorList } from '../tags/TagEditorList';
import { TagEditorEntity } from '../../models/tag.model';
import { PageChangeEvent, Pager } from '@progress/kendo-react-data-tools';
import { PAGING_BUTTON_COUNT, PAGING_OPTIONS } from '../../constants';
import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { selectPagination, setPagination } from '../../store/slices/pagination.slice';
import { WidgetID } from '../../models/widget.model';
import { RootState } from '../../store';
import { useGridWithGrouping } from '../datagrid/hooks/useGridWithGrouping';

export type GridCardType = {
  topRowParams: {
    name: string;
    sequence: number;
    callback?: (...params: any[]) => any;
  }[];
  descriptionTitle: string;
  descriptionBody: string;
  // TODO change this type when is known
  tags: string;
};

type CardGridProps = {
  data: any;
  card: GridCardType;
  widgetId: WidgetID;
  total: number;
  isSelectable?: boolean;
  className?: string;
};

type CardItem = {
  [key: string]: any;
};

function CardRow({ item, card }: any) {
  return (
    <div className="cards-grid-container">
      <div className="data-container">
        <div className="row row-params">
          {card.topRowParams.map((prop: any, index: number): any => {
            if (Object.prototype.hasOwnProperty.call(item, prop.name)) {
              if (prop.callback) {
                return (
                  <div className="cell" key={item[prop.name] + index}>
                    {prop.callback(item[prop.name])}
                  </div>
                );
              } else {
                return (
                  <div className="cell" key={item[prop.name] + index}>
                    {item[prop.name]}
                  </div>
                );
              }
            }
          })}
        </div>
        <div className="row row-descr">
          <div className="cell">
            <p className="p-title">{item[card.descriptionTitle]}</p>
            <p>{item[card.descriptionBody]}</p>
          </div>
        </div>
        <div className="row">
          <div className="cell">
            <KwelloSvgIcon icon={KWELLO_ICON.TAG_OUTLINE_ICON} />
            <TagEditorList
              objectType={TagEditorEntity.RESPONSE}
              id={item.responseId}
              tags={item.tags}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function flattenGroups(groups: any[]): CardItem[] {
  return groups.reduce((acc: CardItem[], group: any) => {
    if (group.items && Array.isArray(group.items)) {
      return [...acc, ...flattenGroups(group.items)] as CardItem[]; // Recursively process nested groups
    }
    return [...acc, group] as CardItem[]; // If no sub-items, just add the group
  }, []);
}

function CardsGrid(props: CardGridProps) {
  const { data, card, total } = props;
  const dispatch = useAppDispatch();
  const pagination = useAppSelector((state: RootState) => selectPagination(state, props.widgetId));
  const { grouping } = useGridWithGrouping(props.widgetId);
  const pageChange = useCallback(
    (event: PageChangeEvent) => {
      const targetEvent = event.targetEvent;
      const take = targetEvent.value === 'All' ? total : event.take;
      dispatch(
        setPagination({
          id: props.widgetId,
          skip: event.skip,
          take: Number(take),
          total: Number(total),
        })
      );
    },
    [total, dispatch, props.widgetId]
  );
  const iterator = grouping.length > 0 ? flattenGroups(data.data) : data.data;

  return (
    <div className={`cards-grid-wrapper ${props.className ?? ''}`}>
      {iterator.map((item: any, index: number): any => (
        <CardRow key={index} item={item} card={card} />
      ))}
      <Pager
        skip={Number(pagination?.skip) || 0}
        take={pagination?.take || PAGING_OPTIONS[0]}
        total={total}
        buttonCount={PAGING_BUTTON_COUNT}
        pageSizes={PAGING_OPTIONS}
        onPageChange={pageChange}
        previousNext={true}
      />
    </div>
  );
}
export default CardsGrid;
